import React from 'react';
import { MessageCircle, Eye, Activity, MessageSquare, Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { SectionHeader } from './SectionHeader';

// Recent Posts Table Component
export function RecentPostsTable({ 
  filteredPosts, 
  paginatedPosts, 
  postsPage, 
  setPostsPage,
  totalPages,
  postsPerPage,
  subscriberCount,
  dateRangeLabel,
  onPostClick
}) {
  return (
    <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
      <SectionHeader 
        icon={MessageCircle} 
        title="Recent Posts Performance" 
        subtitle={`${filteredPosts.length} posts • ${dateRangeLabel}`}
        action={
          totalPages > 1 && (
            <div className="flex items-center gap-2 text-xs text-[#9CA3AF]">
              <span>Page {postsPage} of {totalPages}</span>
            </div>
          )
        }
      />
      
      <div className="p-5">
        {filteredPosts.length > 0 ? (
          <div className="space-y-3">
            {/* Posts Table Header */}
            <div className="grid grid-cols-12 gap-3 px-4 py-2 text-xs text-[#6B7280] uppercase tracking-wider border-b border-[hsl(var(--border))]">
              <div className="col-span-5">Content</div>
              <div className="col-span-2 text-center">Views</div>
              <div className="col-span-2 text-center">Reach %</div>
              <div className="col-span-2 text-center">Reactions</div>
              <div className="col-span-1 text-center">ERR</div>
            </div>
            
            {/* Posts List - Using paginatedPosts */}
            {paginatedPosts.map((post, idx) => {
              const postReach = subscriberCount > 0 ? ((post.views || 0) / subscriberCount * 100) : 0;
              const reactionCount = post.reactions ? Object.values(post.reactions).reduce((a, b) => typeof b === 'number' ? a + b : a, 0) : 0;
              const postErr = (post.views || 0) > 0 ? (reactionCount / (post.views || 1) * 100) : 0;
              
              return (
                <div 
                  key={idx} 
                  className="grid grid-cols-12 gap-3 items-center px-4 py-3 bg-[hsl(var(--surface-elevated))] rounded-lg border border-[hsl(var(--border))] hover:border-[#E63946]/30 hover:bg-[hsl(var(--surface))]/50 transition-colors cursor-pointer" 
                  data-testid={`post-row-${idx}`}
                  onClick={() => onPostClick({ ...post, reactionCount, postReach, postErr })}
                >
                  {/* Content */}
                  <div className="col-span-5 flex items-center gap-3">
                    <div className={`w-8 h-8 rounded flex items-center justify-center shrink-0 ${
                      post.has_photo ? 'bg-[#8B5CF6]/20' : post.has_video ? 'bg-[#EC4899]/20' : 'bg-[#06B6D4]/20'
                    }`}>
                      {post.has_photo ? (
                        <Eye size={14} className="text-[#8B5CF6]" />
                      ) : post.has_video ? (
                        <Activity size={14} className="text-[#EC4899]" />
                      ) : (
                        <MessageSquare size={14} className="text-[#06B6D4]" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">
                        {post.text || post.caption || (post.has_photo ? 'Photo post' : post.has_video ? 'Video post' : 'Text post')}
                      </p>
                      <p className="text-[10px] text-[#6B7280]">
                        {post.date ? format(new Date(post.date), 'dd/MM/yyyy HH:mm') : 'Unknown'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Views */}
                  <div className="col-span-2 text-center">
                    <p className="text-sm font-medium text-white">{(post.views || 0).toLocaleString()}</p>
                  </div>
                  
                  {/* Reach % */}
                  <div className="col-span-2 text-center">
                    <Badge className={`text-xs ${postReach >= 10 ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#6B7280]/20 text-[#6B7280]'}`}>
                      {postReach.toFixed(1)}%
                    </Badge>
                  </div>
                  
                  {/* Reactions */}
                  <div className="col-span-2 text-center flex items-center justify-center gap-1">
                    <Heart size={12} className="text-[#E63946]" />
                    <span className="text-sm text-white">{reactionCount}</span>
                  </div>
                  
                  {/* ERR */}
                  <div className="col-span-1 text-center">
                    <Badge className={`text-[10px] ${postErr >= 1 ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#6B7280]/20 text-[#6B7280]'}`}>
                      {postErr.toFixed(1)}%
                    </Badge>
                  </div>
                </div>
              );
            })}
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t border-[hsl(var(--border))]">
                <p className="text-xs text-[#6B7280]">
                  Showing {((postsPage - 1) * postsPerPage) + 1} - {Math.min(postsPage * postsPerPage, filteredPosts.length)} of {filteredPosts.length} posts
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPostsPage(p => Math.max(1, p - 1))}
                    disabled={postsPage === 1}
                    className="h-8 px-2"
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (postsPage <= 3) {
                      pageNum = i + 1;
                    } else if (postsPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = postsPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={postsPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPostsPage(pageNum)}
                        className={`h-8 w-8 p-0 ${postsPage === pageNum ? 'bg-[#E63946] hover:bg-[#E63946]/90' : ''}`}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPostsPage(p => Math.min(totalPages, p + 1))}
                    disabled={postsPage === totalPages}
                    className="h-8 px-2"
                  >
                    <ChevronRight size={16} />
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-[#6B7280]">
            <MessageCircle size={48} className="mx-auto mb-4 opacity-50" />
            <p className="text-sm">No posts found in selected date range</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default RecentPostsTable;
